import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import {
  useAppProviderContext,
  useConfigurationContext,
  useDropOffLocationContext,
  useModalContext
} from '../../core/application/contexts'
import {
  CreateDropoffFormErrors,
  validateDropoffContactForm,
  validateDropoffCreatorForm,
  validateDropoffInfosForm,
  validateDropoffLocationForm
} from '../../core/application/formValidation/createDropoffFormValidator'
import { useMediaQuery } from '../../core/application/hooks'
import { breakpoints, deviceSizes } from '../../core/application/theme/breakpoints'
import {
  NewDropoff,
  NewDropoffContact,
  NewDropoffCreator,
  NewDropoffInfos,
  NewDropoffLocation
} from '../../core/domain/models/NewDropoff'
import { CloseIcon, LeftArrowIcon } from '../icons'
import NavigationButtons from './NavigationButtons'
import SubmitDropoffOpeningHours from './steps/openingHours/SubmitDropoffOpeningHours'
import SubmitDropoffContact from './steps/SubmitDropoffContact'
import SubmitDropoffCreator from './steps/SubmitDropoffCreator'
import SubmitDropoffInfos from './steps/SubmitDropoffInfos'
import SubmitDropoffLocation from './steps/SubmitDropoffLocation'
import SubmitDropoffPhotos from './steps/SubmitDropoffPhotos'
import SubmitDropoffSuccess from './steps/SubmitDropoffSuccess'
import SubmitDropoffWastes from './steps/SubmitDropoffWastes'
import {
  createDropoffMobileHeaderTitle,
  CreateDropoffStep,
  createDropoffStepNextButtonTitle,
  createDropoffStepPreviousButtonTitle,
  createDropoffStepTitle
} from './SubmitDropoffStep'

const SubmitDropoffDialog: React.FC = () => {
  const intl = useIntl()
  const isMobile = useMediaQuery(parseInt(deviceSizes.tablet))
  const { openMessageModal } = useModalContext()

  const { setDisplaySubmitDropoffScreen } = useDropOffLocationContext()
  const { newDropoffProvider } = useAppProviderContext()
  const { tenantId } = useConfigurationContext()

  const [newDropoff, setNewDropoff] = useState<NewDropoff>({})
  const [currentStep, setCurrentStep] = useState<CreateDropoffStep>(CreateDropoffStep.CREATOR)
  // Steps where user has pressed "next" button at least once. We don't display errors until then.
  const [stepsWithInitialValidation, setStepsWithInitialValidation] = useState<Set<number>>(new Set<number>())
  // Object containing all current errors in the form
  const [formErrors, setFormErrors] = useState<CreateDropoffFormErrors>({})
  // Each step may have a details screen
  const [displayDetailsScreen, setDisplayDetailsScreen] = useState<boolean>(false)
  // Submit request in progress
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Navigation

  const closeDialog = () => {
    setDisplaySubmitDropoffScreen(false)
  }

  const onExitButtonClick = () => {
    if (displayDetailsScreen) {
      setDisplayDetailsScreen(false)
    } else {
      closeDialog()
    }
  }

  const onPreviousClick = () => {
    if (currentStep === CreateDropoffStep.CREATOR) {
      closeDialog()
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const onNextClick = () => {
    // Display errors if needed
    setStepsWithInitialValidation(new Set(stepsWithInitialValidation).add(currentStep))

    if (currentStep === CreateDropoffStep.SUCCESS) {
      closeDialog()
    } else if (currentStep === CreateDropoffStep.PHOTOS) {
      submitForm()
    } else if (validateCurrentStep(newDropoff)) {
      // Only move to next step if no error
      setCurrentStep(currentStep + 1)
    }
  }

  const submitForm = () => {
    setIsLoading(true)
    newDropoffProvider
      ?.createDropoff(newDropoff, tenantId || '')
      .then((dropoffId) => {
        if (newDropoff.photos && newDropoff.photos.photos.length > 0) {
          // Upload pictures
          const requestPromises = newDropoff.photos.photos.map((file) => {
            return newDropoffProvider.uploadDropoffPicture(file, dropoffId)
          })
          Promise.all(requestPromises)
            .then(() => {
              setIsLoading(false)
              setCurrentStep(CreateDropoffStep.SUCCESS)
            })
            .catch((error) => {
              // Fail open
              console.warn('Failed to upload pictures: ', error)
              setIsLoading(false)
              setCurrentStep(CreateDropoffStep.SUCCESS)
            })
        } else {
          setIsLoading(false)
          setCurrentStep(CreateDropoffStep.SUCCESS)
        }
      })
      .catch((error) => {
        console.error(error)
        setIsLoading(false)
        openMessageModal(intl.formatMessage({ id: 'SubmitDropoff.error.default' }))
      })
  }

  // Fields validation

  const validateCurrentStep: (dropoff: NewDropoff) => boolean = (dropoff) => {
    switch (currentStep) {
      case CreateDropoffStep.CREATOR:
        const creator = validateDropoffCreatorForm(dropoff.creator)
        setFormErrors({
          ...formErrors,
          creator
        })
        return creator === undefined
      case CreateDropoffStep.INFOS:
        const infos = validateDropoffInfosForm(dropoff.infos)
        setFormErrors({
          ...formErrors,
          infos
        })
        return infos === undefined
      case CreateDropoffStep.CONTACT:
        const contact = validateDropoffContactForm(dropoff.contact)
        setFormErrors({
          ...formErrors,
          contact
        })
        return contact === undefined
      case CreateDropoffStep.LOCATION:
        const location = validateDropoffLocationForm(dropoff.location)
        setFormErrors({
          ...formErrors,
          location
        })
        return location === undefined
    }
    return true
  }

  // Dataset update

  const updateCreator = (creator: NewDropoffCreator) => {
    const updated = {
      ...newDropoff,
      creator
    }
    setNewDropoff(updated)
    validateCurrentStep(updated)
  }

  const updateInfos = (infos: NewDropoffInfos) => {
    const updated = {
      ...newDropoff,
      infos
    }
    setNewDropoff(updated)
    validateCurrentStep(updated)
  }

  const updateContact = (contact: NewDropoffContact) => {
    const updated = {
      ...newDropoff,
      contact
    }
    setNewDropoff(updated)
    validateCurrentStep(updated)
  }

  const updateLocation = (location: NewDropoffLocation) => {
    const updated = {
      ...newDropoff,
      location
    }
    setNewDropoff(updated)
    validateCurrentStep(updated)
  }

  const updatePhotos = (photos: File[]) => {
    const updated = {
      ...newDropoff,
      photos: {
        photos
      }
    }
    setNewDropoff(updated)
  }

  // Render

  const renderCurrentStep = () => {
    switch (currentStep) {
      case CreateDropoffStep.CREATOR:
        return (
          <SubmitDropoffCreator
            creator={newDropoff.creator}
            updateCreator={updateCreator}
            errors={formErrors.creator}
            shouldDisplayErrors={stepsWithInitialValidation.has(currentStep)}
          />
        )
      case CreateDropoffStep.INFOS:
        return (
          <SubmitDropoffInfos
            infos={newDropoff.infos}
            updateInfos={updateInfos}
            errors={formErrors.infos}
            shouldDisplayErrors={stepsWithInitialValidation.has(currentStep)}
            displayWasteTypesSelection={() => setDisplayDetailsScreen(true)}
          />
        )
      case CreateDropoffStep.LOCATION:
        return (
          <SubmitDropoffLocation
            location={newDropoff.location}
            updateLocation={updateLocation}
            errors={formErrors.location}
            shouldDisplayErrors={stepsWithInitialValidation.has(currentStep)}
          />
        )
      case CreateDropoffStep.CONTACT:
        return (
          <SubmitDropoffContact
            contact={newDropoff.contact}
            updateContact={updateContact}
            errors={formErrors.contact}
            shouldDisplayErrors={stepsWithInitialValidation.has(currentStep)}
            displayOpeningTimeSelection={() => setDisplayDetailsScreen(true)}
          />
        )
      case CreateDropoffStep.PHOTOS:
        return <SubmitDropoffPhotos selectedFiles={newDropoff.photos?.photos || []} setSelectedFiles={updatePhotos} />
      case CreateDropoffStep.SUCCESS:
        return <SubmitDropoffSuccess />
    }
  }

  const renderButtons = () => {
    let previousStepIntl = createDropoffStepPreviousButtonTitle(currentStep)
    let nextStepIntl = createDropoffStepNextButtonTitle(currentStep)

    return (
      <NavigationButtons
        previousTitle={previousStepIntl ? intl.formatMessage({ id: previousStepIntl }) : undefined}
        nextTitle={intl.formatMessage({ id: nextStepIntl })}
        onPreviousClick={onPreviousClick}
        onNextClick={onNextClick}
        isLoading={isLoading}
      />
    )
  }

  const renderDetailsScreen = () => {
    switch (currentStep) {
      case CreateDropoffStep.INFOS:
        return (
          <SubmitDropoffWastes
            currentlySelectedTypes={newDropoff.infos?.wasteCategories || []}
            onDismiss={() => setDisplayDetailsScreen(false)}
            onValidate={(wasteTypes) => {
              const updated = {
                ...newDropoff,
                infos: {
                  ...newDropoff.infos,
                  wasteCategories: wasteTypes
                }
              }
              validateCurrentStep(updated)
              setNewDropoff(updated)
              setDisplayDetailsScreen(false)
            }}
          />
        )
      case CreateDropoffStep.CONTACT:
        return (
          <SubmitDropoffOpeningHours
            currentOpeningHours={newDropoff.contact?.openingHours}
            onDismiss={() => setDisplayDetailsScreen(false)}
            onValidate={(openingHours) => {
              const updatedContact = {
                ...(newDropoff.contact || {
                  openingHours: [],
                  phone: '',
                  website: ''
                }),
                openingHours
              }
              const updated = {
                ...newDropoff,
                contact: updatedContact
              }
              setNewDropoff(updated)
              validateCurrentStep(updated)
              setDisplayDetailsScreen(false)
            }}
          />
        )
      default:
        return <></>
    }
  }

  const renderDesktopHeader = () => {
    return (
      <>
        <Header>
          <StepLabel>
            {!displayDetailsScreen &&
              currentStep !== CreateDropoffStep.SUCCESS &&
              intl.formatMessage({ id: 'SubmitDropoff.step' }, { step: currentStep + 1 })}
          </StepLabel>
          <CloseButton onClick={onExitButtonClick}>
            {displayDetailsScreen ? <LeftArrowIcon /> : <CloseIcon />}
          </CloseButton>
        </Header>
        <Title>{intl.formatMessage({ id: createDropoffStepTitle(currentStep, displayDetailsScreen, false) })}</Title>
      </>
    )
  }

  const renderMobileHeader = () => {
    const titleIntlKey = createDropoffStepTitle(currentStep, displayDetailsScreen, true)
    return (
      <>
        <Header>
          <CloseButton onClick={onExitButtonClick}>
            {displayDetailsScreen ? <LeftArrowIcon /> : <CloseIcon />}
          </CloseButton>
          <HeaderTitle>
            {intl.formatMessage({ id: createDropoffMobileHeaderTitle(currentStep, displayDetailsScreen) })}
          </HeaderTitle>
          <StepLabel>
            {!displayDetailsScreen &&
              currentStep !== CreateDropoffStep.SUCCESS &&
              intl.formatMessage({ id: 'SubmitDropoff.step' }, { step: currentStep + 1 })}
          </StepLabel>
        </Header>
        {titleIntlKey && <Title>{intl.formatMessage({ id: titleIntlKey })}</Title>}
      </>
    )
  }

  return (
    <SubmitDropoffDialogContainer>
      {isMobile ? renderMobileHeader() : renderDesktopHeader()}
      {!displayDetailsScreen && renderCurrentStep()}
      {!displayDetailsScreen && renderButtons()}
      {displayDetailsScreen && renderDetailsScreen()}
    </SubmitDropoffDialogContainer>
  )
}

const SubmitDropoffDialogContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 24px;
  background: ${({ theme }) => theme.color.white};
  box-shadow: 8px 0px 16px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  border-radius: 0px;

  @media ${breakpoints.tablet} {
    width: 60vw;
    max-width: 620px;
    height: auto;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
  }
`

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 14px;
  position: relative;
`

const StepLabel = styled.div`
  text-align: left;
  font-style: normal;
  font-weight: ${({ theme }) => theme.weight.normal};
  font-size: ${({ theme }) => theme.size.small};
  color: ${({ theme }) => theme.color.black};
`

const CloseButton = styled.button`
  cursor: pointer;
  z-index: 5;
`

const Title = styled.div`
  text-align: left;
  font-style: normal;
  font-family: ${({ theme }) => theme.font.semi};
  font-weight: ${({ theme }) => theme.weight.semi};
  font-size: ${({ theme }) => theme.size.big};
  color: ${({ theme }) => theme.color.black};
  margin-bottom: 2px;
`

const HeaderTitle = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  text-align: center;
  font-style: normal;
  font-family: ${({ theme }) => theme.font.semi};
  font-weight: ${({ theme }) => theme.weight.semi};
  font-size: ${({ theme }) => theme.size.large};
  color: ${({ theme }) => theme.color.black};
`

export default SubmitDropoffDialog
